// src/services/adminApi.ts
//
// Frontend client for the System Admin endpoints. Routes through the
// Netlify proxy at /api/admin/* which validates the Auth0 JWT, checks
// tenant access, runs the suspend gate, and forwards to the right
// agent with X-API-Key + X-Actor-Email + X-Actor-Sub.
//
// Token + tenant are injected the same way as apiV2 — see useAuth's
// useEffect setup that calls setAuthToken/setTenantSlug after Auth0
// resolves.

import axios, { type AxiosInstance, type AxiosResponse } from 'axios';
import type {
  AdminEnvelope,
  AdminHealthStatus,
  AdditionalUserEntry,
  AuditEntry,
  EffectiveFeatures,
  FeatureEntry,
  MergedUsersResponse,
  SchedulerEntry,
  TenantConfigEntry,
  UserOverrideEntry,
} from '../types/admin';

const BASE = '/api/admin';

function buildClient(token?: string | null, tenantSlug?: string | null): AxiosInstance {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (tenantSlug) headers['x-tenant-slug'] = tenantSlug;
  return axios.create({ baseURL: '', timeout: 30000, headers });
}

class AdminApiService {
  private _client: AxiosInstance;
  private _token: string | null = null;
  private _slug: string | null = null;

  constructor() {
    this._client = buildClient();
  }

  setAuthToken(token: string | null) {
    this._token = token;
    this._client = buildClient(this._token, this._slug);
  }

  setTenantSlug(slug: string | null) {
    this._slug = slug;
    this._client = buildClient(this._token, this._slug);
  }

  /**
   * Returns the right axios client for a request. If the caller passed an
   * explicit slug AND it differs from the configured default, build a
   * one-off client carrying that slug (so e.g. SystemAdminPage can browse
   * a non-active tenant without mutating the global state). Otherwise
   * reuse the default client.
   */
  private cli(slug?: string | null): AxiosInstance {
    if (slug && slug !== this._slug) return buildClient(this._token, slug);
    return this._client;
  }

  // ── health ───────────────────────────────────────────────────────

  async getHealth(slug?: string): Promise<AdminHealthStatus> {
    return this.unwrap(this.cli(slug).get<AdminEnvelope<AdminHealthStatus>>(`${BASE}/health`));
  }

  // ── tenant_config ────────────────────────────────────────────────

  async getAllConfig(slug?: string): Promise<TenantConfigEntry[]> {
    return this.unwrap(this.cli(slug).get<AdminEnvelope<TenantConfigEntry[]>>(`${BASE}/config`));
  }

  async getConfig(key: string, slug?: string): Promise<TenantConfigEntry | null> {
    try {
      return await this.unwrap(this.cli(slug).get<AdminEnvelope<TenantConfigEntry>>(`${BASE}/config/${encodeURIComponent(key)}`));
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) return null;
      throw e;
    }
  }

  async putConfig(key: string, value: string | null, valueType: string | null = 'string', slug?: string): Promise<TenantConfigEntry> {
    return this.unwrap(this.cli(slug).put<AdminEnvelope<TenantConfigEntry>>(
      `${BASE}/config/${encodeURIComponent(key)}`,
      { value, valueType },
    ));
  }

  // ── features ─────────────────────────────────────────────────────

  async getAllFeatures(slug?: string): Promise<FeatureEntry[]> {
    return this.unwrap(this.cli(slug).get<AdminEnvelope<FeatureEntry[]>>(`${BASE}/features`));
  }

  async putFeature(featureKey: string, isEnabled: boolean, slug?: string): Promise<FeatureEntry> {
    return this.unwrap(this.cli(slug).put<AdminEnvelope<FeatureEntry>>(
      `${BASE}/features/${encodeURIComponent(featureKey)}`,
      { isEnabled },
    ));
  }

  async getEffectiveFeatures(userSub: string, slug?: string): Promise<string[]> {
    const res = await this.unwrap(this.cli(slug).get<AdminEnvelope<EffectiveFeatures>>(
      `${BASE}/features/effective?user=${encodeURIComponent(userSub)}`,
    ));
    return res.enabledFeatures;
  }

  // ── user_overrides ───────────────────────────────────────────────

  async getAllUserOverrides(user?: string, slug?: string): Promise<UserOverrideEntry[]> {
    const url = user ? `${BASE}/user-overrides?user=${encodeURIComponent(user)}` : `${BASE}/user-overrides`;
    return this.unwrap(this.cli(slug).get<AdminEnvelope<UserOverrideEntry[]>>(url));
  }

  async putUserOverride(auth0Sub: string, featureKey: string, isEnabled: boolean, slug?: string): Promise<void> {
    await this.unwrap(this.cli(slug).put<AdminEnvelope<void>>(
      `${BASE}/user-overrides/${encodeURIComponent(auth0Sub)}/${encodeURIComponent(featureKey)}`,
      { isEnabled },
    ));
  }

  async deleteUserOverride(auth0Sub: string, featureKey: string, slug?: string): Promise<boolean> {
    try {
      await this.unwrap(this.cli(slug).delete<AdminEnvelope<void>>(
        `${BASE}/user-overrides/${encodeURIComponent(auth0Sub)}/${encodeURIComponent(featureKey)}`,
      ));
      return true;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) return false;
      throw e;
    }
  }

  // ── additional_users ─────────────────────────────────────────────

  async getAllUsers(slug?: string): Promise<AdditionalUserEntry[]> {
    return this.unwrap(this.cli(slug).get<AdminEnvelope<AdditionalUserEntry[]>>(`${BASE}/users`));
  }

  /**
   * PR1 — Returns every member of a tenant: Auth0 users whose
   * app_metadata.tenants includes this slug, merged with their
   * additional_users row on the agent. Catches legacy users created via
   * the genireports-admin site or directly in Auth0 that have no
   * additional_users row yet.
   *
   * Sysadmin-only on the server side. Goes through a dedicated
   * /api/users/list function (NOT through admin-proxy) because it needs
   * the Auth0 Management API M2M credentials.
   */
  async listMembers(slug?: string): Promise<MergedUsersResponse> {
    const targetSlug = slug ?? this._slug ?? '';
    const r = await this.cli(slug).get<AdminEnvelope<unknown> & {
      data?: unknown;
      counts?: MergedUsersResponse['counts'];
      warnings?: string[];
    }>('/api/users/list', {
      // Always force the slug header even if there's no global default,
      // since this endpoint requires it.
      headers: targetSlug ? { 'x-tenant-slug': targetSlug } : undefined,
    });
    if (!r.data.success) {
      throw new Error(r.data.error || 'Failed to fetch members');
    }
    return {
      users: (r.data.data as MergedUsersResponse['users']) ?? [],
      counts: r.data.counts ?? { total: 0, auth0Only: 0, agentOnly: 0, both: 0 },
      warnings: r.data.warnings ?? [],
    };
  }

  async getUser(auth0Sub: string, slug?: string): Promise<AdditionalUserEntry | null> {
    try {
      return await this.unwrap(this.cli(slug).get<AdminEnvelope<AdditionalUserEntry>>(
        `${BASE}/users/${encodeURIComponent(auth0Sub)}`,
      ));
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) return null;
      throw e;
    }
  }

  async inviteUser(auth0Sub: string, email: string, role: string, slug?: string): Promise<AdditionalUserEntry> {
    return this.unwrap(this.cli(slug).post<AdminEnvelope<AdditionalUserEntry>>(`${BASE}/users`, {
      auth0Sub, email, role,
    }));
  }

  /**
   * WS-E.2 — One-shot Auth0 invite. Creates the Auth0 user, sets tenant
   * access in app_metadata, optionally assigns an Auth0 role, generates a
   * password-change ticket, and inserts into the agent's additional_users.
   * Returns the ticket URL so the inviting admin can share it manually.
   */
  async createUserInvite(args: {
    email: string;
    firstName?: string;
    lastName?: string;
    tenantRole: 'user' | 'account_admin';
    auth0Role?: string;
  }, slug?: string): Promise<{
    auth0Sub: string;
    email: string;
    tenantRole: string;
    assignedRole: string | null;
    userExisted: boolean;
    ticketUrl: string | null;
    emailSent: boolean;
    emailError: string | null;
    tenantSlug: string;
    agentInsertWarning: string | null;
  }> {
    return this.unwrap(this.cli(slug).post<AdminEnvelope<{
      auth0Sub: string;
      email: string;
      tenantRole: string;
      assignedRole: string | null;
      userExisted: boolean;
      ticketUrl: string | null;
      emailSent: boolean;
      emailError: string | null;
      tenantSlug: string;
      agentInsertWarning: string | null;
      // Mirror meitev: send THIS app's origin explicitly so the password
      // ticket's result_url (post-reset redirect) is per-app, instead of
      // relying on the request's origin header (stripped through the proxy).
    }>>('/api/users/invite', { ...args, appBaseUrl: window.location.origin }));
  }

  /**
   * Edit an existing user. Per-field semantics:
   *   undefined / empty string -> leave alone
   *   non-empty string         -> set to that value
   *
   * `tenantRole` upserts the agent's additional_users row (preserves
   * invited_at / last_seen) and requires `email` so the agent has the
   * full body. Pass the user's existing email here — it's not actually
   * being changed.
   *
   * Auth0 fields (firstName/lastName/auth0Role) hit the Auth0 Mgmt API.
   */
  async updateUser(args: {
    auth0Sub: string;
    firstName?: string;
    lastName?: string;
    auth0Role?: string;
    tenantRole?: 'user' | 'account_admin';
    email?: string;
  }, slug?: string): Promise<{
    auth0Sub: string;
    updated: {
      firstName: string | null;
      lastName: string | null;
      auth0Role: { added: string | null; removed: string[] } | null;
      tenantRole: string | null;
    };
    warnings: string[];
  }> {
    return this.unwrap(this.cli(slug).post<AdminEnvelope<{
      auth0Sub: string;
      updated: {
        firstName: string | null;
        lastName: string | null;
        auth0Role: { added: string | null; removed: string[] } | null;
        tenantRole: string | null;
      };
      warnings: string[];
    }>>('/api/users/update', args));
  }

  async revokeUser(auth0Sub: string, slug?: string): Promise<boolean> {
    try {
      await this.unwrap(this.cli(slug).delete<AdminEnvelope<void>>(`${BASE}/users/${encodeURIComponent(auth0Sub)}`));
      return true;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) return false;
      throw e;
    }
  }

  // ── audit_local ──────────────────────────────────────────────────

  async getAudit(opts: { from?: Date; to?: Date; limit?: number; slug?: string } = {}): Promise<AuditEntry[]> {
    const qs: string[] = [];
    if (opts.from) qs.push(`from=${encodeURIComponent(opts.from.toISOString())}`);
    if (opts.to) qs.push(`to=${encodeURIComponent(opts.to.toISOString())}`);
    if (opts.limit) qs.push(`limit=${opts.limit}`);
    const url = `${BASE}/audit${qs.length ? `?${qs.join('&')}` : ''}`;
    return this.unwrap(this.cli(opts.slug).get<AdminEnvelope<AuditEntry[]>>(url));
  }

  // ── scheduler_config ─────────────────────────────────────────────

  async getAllScheduler(slug?: string): Promise<SchedulerEntry[]> {
    return this.unwrap(this.cli(slug).get<AdminEnvelope<SchedulerEntry[]>>(`${BASE}/scheduler`));
  }

  async upsertScheduler(eventId: string, body: {
    sendType: string;
    timeOfDay?: string | null;
    daysOfWeek?: string | null;
    timeZone?: string | null;
    active: boolean;
  }, slug?: string): Promise<SchedulerEntry> {
    return this.unwrap(this.cli(slug).put<AdminEnvelope<SchedulerEntry>>(
      `${BASE}/scheduler/${encodeURIComponent(eventId)}`,
      body,
    ));
  }

  async deleteScheduler(eventId: string, slug?: string): Promise<boolean> {
    try {
      await this.unwrap(this.cli(slug).delete<AdminEnvelope<void>>(
        `${BASE}/scheduler/${encodeURIComponent(eventId)}`,
      ));
      return true;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) return false;
      throw e;
    }
  }

  // ── tenant directory (Supabase tenants table — separate route) ───
  //
  // These hit /api/tenants/* (not /api/admin/*). The same Auth0 bearer is
  // used; the Netlify function enforces the GeniReports Admin role before
  // writing to Supabase.

  async setTenantStatus(slug: string, active: boolean, suspensionReason?: string | null): Promise<{
    slug: string;
    display_name: string;
    active: boolean;
    suspension_reason: string | null;
  }> {
    return this.unwrap(this.cli().patch<AdminEnvelope<{
      slug: string;
      display_name: string;
      active: boolean;
      suspension_reason: string | null;
    }>>(
      `/api/tenants/${encodeURIComponent(slug)}/status`,
      { active, suspension_reason: suspensionReason ?? null },
    ));
  }

  // ── plumbing ─────────────────────────────────────────────────────

  private async unwrap<T>(p: Promise<AxiosResponse<AdminEnvelope<T>>>): Promise<T> {
    const res = await p;
    if (!res.data?.success) {
      throw new Error(res.data?.error || `Admin API request failed (status ${res.status})`);
    }
    return res.data.data as T;
  }
}

export const adminApi = new AdminApiService();
